import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Textarea';
import { useToast } from '../../components/ui/Toast';
import { formatEUR, formatDate } from '../../lib/format';
import { RecordPaymentDialog } from '../../components/collection/RecordPaymentDialog';
import {
  Tabs,
  Modal,
  Empty,
  StatePill,
  labelCls,
  thCls,
  thNum,
  tdCls,
  tdNum,
  apiMessage,
  cmd,
  useCollectionRole,
  type Instrument,
} from '../../components/collection/collectionShared';

/**
 * Encaissements — every euro received: where it came from, which invoices it settles, and where it
 * stands at the bank. Issued LCRs are remitted together on a slip; LCRs and direct debits mature by
 * themselves overnight once their date has passed; an instrument the bank returns is rejected here,
 * which re-opens its invoices.
 */

interface Other {
  id: string;
  customerId: string;
  customerName: string;
  mode: string;
  amount: number;
  reference: string | null;
  date: string;
  invoiceNumber: string | null;
  pending: boolean;
  reversed: boolean;
  by: string | null;
}
interface Data {
  instruments: Instrument[];
  /** Money recorded outside a règlement (before migration 151, bank-feed matches). */
  other: Other[];
  remittances: Array<{
    id: string;
    number: string;
    date: string;
    kind: string;
    total: number;
    count: number;
    by: string | null;
    items: string[];
  }>;
}
type Tab = 'liste' | 'autres' | 'remise' | 'banque' | 'bordereaux';
const REJECTABLE = new Set(['EMISE', 'REMISE', 'ECHUE', 'PROGRAMME', 'PRESENTE', 'PAYE']);

export default function CollectionPayments() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const { collector } = useCollectionRole();
  const [data, setData] = useState<Data | null>(null);
  const [tab, setTab] = useState<Tab>('liste');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pay, setPay] = useState(false);
  const [detail, setDetail] = useState<Instrument | null>(null);
  const [rejecting, setRejecting] = useState<Instrument | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<Data>('/collection/instruments')
      .then((r) => setData(r.data))
      .catch((e) => showApiError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const all = useMemo(() => data?.instruments ?? [], [data]);
  const toRemit = useMemo(() => all.filter((p) => p.mode === 'LCR' && p.state === 'EMISE'), [all]);
  const inBank = useMemo(
    () => all.filter((p) => ['REMISE', 'PRESENTE', 'PROGRAMME'].includes(p.state)),
    [all]
  );

  const remit = async () => {
    try {
      const r = await apiClient.post<{ number: string; count: number; total: number }>(
        '/collection/remittances',
        { instrumentIds: [...picked] },
        cmd()
      );
      push({
        tone: 'success',
        message: t('collection.payments.remitted', {
          number: r.data.number,
          count: r.data.count,
          amount: formatEUR(r.data.total),
        }),
      });
      setPicked(new Set());
      load();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };

  const row = (
    p: Instrument,
    cols: Array<'customer' | 'mode' | 'ref' | 'amount' | 'alloc' | 'state' | 'by' | 'due'>
  ) => (
    <tr
      key={p.id}
      className="cursor-pointer border-b last:border-b-0 hover:bg-muted/50"
      onClick={() => setDetail(p)}
    >
      <td className={tdCls}>
        <span className="font-mono text-xs">{p.number}</span>
        <div className="text-xs text-muted-foreground">
          {formatDate(p.date)}
          {p.dueDate ? ` · ${t('collection.overview.dueOn', { date: formatDate(p.dueDate) })}` : ''}
        </div>
      </td>
      {cols.includes('customer') && (
        <td className={tdCls}>
          {p.customerName}
          <div className="text-xs text-muted-foreground">
            {t(`collection.type.${p.customerType}`)}
          </div>
        </td>
      )}
      {cols.includes('mode') && <td className={tdCls}>{t(`collection.mode.${p.mode}`)}</td>}
      {cols.includes('ref') && <td className={tdCls}>{p.reference || '—'}</td>}
      {cols.includes('due') && <td className={tdCls}>{formatDate(p.dueDate)}</td>}
      {cols.includes('amount') && <td className={tdNum}>{formatEUR(p.amount)}</td>}
      {cols.includes('alloc') && (
        <td className={tdCls}>
          {p.allocations.length
            ? p.allocations.map((a) => (
                <div key={a.invoiceId} className="whitespace-nowrap">
                  <span className="font-mono text-xs">{a.number}</span> {formatEUR(a.amount)}
                </div>
              ))
            : '—'}
          {p.onAccount > 0 && (
            <div className="text-xs text-muted-foreground">
              {t('collection.payments.advance', { amount: formatEUR(p.onAccount) })}
            </div>
          )}
        </td>
      )}
      {cols.includes('state') && (
        <td className={tdCls}>
          <StatePill state={p.state} />
          {p.remittance && <div className="text-xs text-muted-foreground">{p.remittance}</div>}
        </td>
      )}
      {cols.includes('by') && <td className={tdCls}>{p.createdBy ?? '—'}</td>}
    </tr>
  );

  const head = (cols: string[]) => (
    <thead>
      <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
        {cols.map((c) => (
          <th key={c} className={c === 'amount' ? thNum : thCls}>
            {c ? t(`collection.col.${c}`) : ''}
          </th>
        ))}
      </tr>
    </thead>
  );

  return (
    <div>
      <PageHeader
        title={t('collection.payments.title')}
        description={t('collection.payments.description')}
        actions={
          <Button size="sm" onClick={() => setPay(true)}>
            <Plus className="h-4 w-4" /> {t('collection.pay.title')}
          </Button>
        }
      />
      <div className="rounded-lg border bg-card shadow-sm">
        <Tabs<Tab>
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'liste', label: t('collection.payments.tab.liste'), count: all.length },
            ...(data?.other.length
              ? [
                  {
                    key: 'autres' as const,
                    label: t('collection.payments.tab.autres'),
                    count: data.other.length,
                  },
                ]
              : []),
            {
              key: 'remise',
              label: t('collection.payments.tab.remise'),
              count: toRemit.length,
              alert: true,
            },
            { key: 'banque', label: t('collection.payments.tab.banque'), count: inBank.length },
            {
              key: 'bordereaux',
              label: t('collection.payments.tab.bordereaux'),
              count: data?.remittances.length ?? 0,
            },
          ]}
        />
        <div className="overflow-x-auto">
          {data === null ? null : tab === 'liste' ? (
            all.length ? (
              <table className="w-full text-sm">
                {head([
                  'payment',
                  'customer',
                  'mode',
                  'reference',
                  'amount',
                  'allocatedTo',
                  'state',
                  'enteredBy',
                ])}
                <tbody>
                  {all.map((p) =>
                    row(p, ['customer', 'mode', 'ref', 'amount', 'alloc', 'state', 'by'])
                  )}
                </tbody>
              </table>
            ) : (
              <Empty>{t('collection.payments.empty')}</Empty>
            )
          ) : tab === 'autres' ? (
            <OtherPayments rows={data.other} canAct={collector} onChanged={load} />
          ) : tab === 'remise' ? (
            toRemit.length ? (
              <>
                <p className="px-4 py-2 text-sm text-muted-foreground">
                  {t('collection.payments.remitNote')}
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="w-10 px-3 py-2" />
                      {['payment', 'customer', 'reference', 'due', 'amount'].map((c) => (
                        <th key={c} className={c === 'amount' ? thNum : thCls}>
                          {t(`collection.col.${c}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {toRemit.map((p) => (
                      <tr key={p.id} className="border-b last:border-b-0">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={picked.has(p.id)}
                            onChange={(e) =>
                              setPicked((s) => {
                                const n = new Set(s);
                                if (e.target.checked) n.add(p.id);
                                else n.delete(p.id);
                                return n;
                              })
                            }
                            aria-label={t('collection.payments.remitOne', { number: p.number })}
                          />
                        </td>
                        <td className={tdCls}>
                          <span className="font-mono text-xs">{p.number}</span>
                        </td>
                        <td className={tdCls}>{p.customerName}</td>
                        <td className={tdCls}>{p.reference}</td>
                        <td className={tdCls}>{formatDate(p.dueDate)}</td>
                        <td className={tdNum}>{formatEUR(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex items-center justify-end gap-3 border-t px-4 py-3">
                  {picked.size > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {t('collection.payments.selected', {
                        count: picked.size,
                        amount: formatEUR(
                          toRemit.filter((p) => picked.has(p.id)).reduce((a, p) => a + p.amount, 0)
                        ),
                      })}
                    </span>
                  )}
                  <Button onClick={remit} disabled={!collector || picked.size === 0}>
                    {t('collection.payments.remit')}
                  </Button>
                </div>
              </>
            ) : (
              <Empty>{t('collection.payments.nothingToRemit')}</Empty>
            )
          ) : tab === 'banque' ? (
            inBank.length ? (
              <>
                <table className="w-full text-sm">
                  {head(['payment', 'customer', 'mode', 'due', 'amount', 'state'])}
                  <tbody>
                    {inBank.map((p) => row(p, ['customer', 'mode', 'due', 'amount', 'state']))}
                  </tbody>
                </table>
                <p className="border-t px-4 py-2 text-sm text-muted-foreground">
                  {t('collection.payments.bankNote')}
                </p>
              </>
            ) : (
              <Empty>{t('collection.payments.nothingInBank')}</Empty>
            )
          ) : data.remittances.length ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  {['slip', 'date', 'kind', 'payments', 'amount', 'by'].map((c) => (
                    <th key={c} className={c === 'amount' ? thNum : thCls}>
                      {t(`collection.col.${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.remittances.map((r) => (
                  <tr key={r.id} className="border-b last:border-b-0">
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{r.number}</span>
                    </td>
                    <td className={tdCls}>{formatDate(r.date)}</td>
                    <td className={tdCls}>{r.kind}</td>
                    <td className={tdCls}>
                      <span className="font-mono text-xs">{r.items.join(', ')}</span>
                    </td>
                    <td className={tdNum}>{formatEUR(r.total)}</td>
                    <td className={tdCls}>{r.by ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>{t('collection.payments.noSlips')}</Empty>
          )}
        </div>
      </div>
      {pay && <RecordPaymentDialog onClose={() => setPay(false)} onDone={load} />}
      {detail && (
        <Modal
          title={`${detail.number} · ${t(`collection.mode.${detail.mode}`)} ${formatEUR(detail.amount)}`}
          lede={`${detail.customerName}${detail.reference ? ` · ${detail.reference}` : ''}`}
          onClose={() => setDetail(null)}
          footer={
            <>
              {collector &&
                (REJECTABLE.has(detail.state) ||
                  (detail.state === 'RECU' &&
                    (detail.mode === 'CHECK' || detail.mode === 'TRANSFER'))) && (
                  <Button
                    variant="destructive"
                    className="mr-auto"
                    onClick={() => {
                      setRejecting(detail);
                      setDetail(null);
                    }}
                  >
                    {t('collection.payments.reject')}
                  </Button>
                )}
              <Button variant="outline" onClick={() => setDetail(null)}>
                {t('collection.common.close')}
              </Button>
            </>
          }
        >
          <div>
            <span className={labelCls}>{t('collection.col.allocatedTo')}</span>
            {detail.allocations.map((a) => (
              <div key={a.invoiceId} className="text-sm">
                <span className="font-mono text-xs">{a.number}</span> · {formatEUR(a.amount)}
              </div>
            ))}
            {detail.onAccount > 0 && (
              <div className="text-sm text-muted-foreground">
                {t('collection.payments.advance', { amount: formatEUR(detail.onAccount) })}
              </div>
            )}
          </div>
          <div>
            <span className={labelCls}>{t('collection.payments.history')}</span>
            <ol className="grid gap-1.5 text-sm">
              {detail.history.map((h, n) => (
                <li key={n} className="grid grid-cols-[96px_1fr] gap-2">
                  <time className="tabular-nums text-muted-foreground">{formatDate(h.date)}</time>
                  <span>
                    <StatePill state={h.state} />{' '}
                    <span className="text-muted-foreground">
                      {h.system ? t('collection.common.system') : h.by}
                    </span>
                    {h.note && <span className="ml-1 text-muted-foreground">· {h.note}</span>}
                  </span>
                </li>
              ))}
            </ol>
          </div>
          {detail.rejectReason && <p className="text-sm text-destructive">{detail.rejectReason}</p>}
        </Modal>
      )}
      {rejecting && (
        <RejectDialog
          p={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function RejectDialog({
  p,
  onClose,
  onDone,
}: {
  p: Instrument;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { push } = useToast();
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const submit = async () => {
    try {
      await apiClient.post(`/collection/instruments/${p.id}/reject`, { reason }, cmd());
      push({ tone: 'success', message: t('collection.payments.rejected', { number: p.number }) });
      onDone();
    } catch (e) {
      setErr(apiMessage(t, e));
    }
  };
  return (
    <Modal
      title={t('collection.payments.rejectTitle', { number: p.number })}
      lede={t('collection.payments.rejectLede')}
      onClose={onClose}
      footer={
        <>
          <p className="mr-auto flex-1 text-sm font-semibold text-destructive" role="alert">
            {err}
          </p>
          <Button variant="outline" onClick={onClose}>
            {t('collection.common.cancel')}
          </Button>
          <Button variant="destructive" onClick={submit} disabled={reason.trim().length < 10}>
            {t('collection.payments.reject')}
          </Button>
        </>
      }
    >
      <label className="block">
        <span className={labelCls}>{t('collection.payments.rejectReason')}</span>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        <span className="mt-1 block text-xs text-muted-foreground">
          {t('collection.common.min10')}
        </span>
      </label>
    </Modal>
  );
}

/**
 * Payments recorded outside a règlement: before migration 151, by the bank-feed matcher, or by the
 * counter's register lane. A provisional one is confirmed here once the bank has paid; a wrong one
 * is cancelled by counter-entry (the invoice re-opens).
 */
function OtherPayments({
  rows,
  canAct,
  onChanged,
}: {
  rows: Other[];
  canAct: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { push } = useToast();
  const [reversing, setReversing] = useState<Other | null>(null);
  const [reason, setReason] = useState('');
  const finalise = async (o: Other) => {
    try {
      await apiClient.post(`/recap-invoices/payments/${o.id}/finalise`, {}, cmd());
      push({ tone: 'success', message: t('collection.payments.confirmed') });
      onChanged();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };
  const reverse = async () => {
    if (!reversing) return;
    try {
      await apiClient.post(`/recap-invoices/payments/${reversing.id}/reverse`, { reason }, cmd());
      push({ tone: 'success', message: t('collection.payments.cancelled') });
      setReversing(null);
      setReason('');
      onChanged();
    } catch (e) {
      push({ tone: 'error', message: apiMessage(t, e) });
    }
  };
  return (
    <>
      <p className="px-4 py-2 text-sm text-muted-foreground">
        {t('collection.payments.otherNote')}
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
            <th className={thCls}>{t('collection.col.date')}</th>
            <th className={thCls}>{t('collection.col.customer')}</th>
            <th className={thCls}>{t('collection.col.mode')}</th>
            <th className={thCls}>{t('collection.col.reference')}</th>
            <th className={thNum}>{t('collection.col.amount')}</th>
            <th className={thCls}>{t('collection.col.allocatedTo')}</th>
            <th className={thCls}>{t('collection.col.state')}</th>
            <th className={thCls} />
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id} className="border-b last:border-b-0">
              <td className={tdCls}>{formatDate(o.date)}</td>
              <td className={tdCls}>{o.customerName}</td>
              <td className={tdCls}>{t(`collection.mode.${o.mode}`, { defaultValue: o.mode })}</td>
              <td className={tdCls}>{o.reference || '—'}</td>
              <td className={tdNum}>{formatEUR(o.amount)}</td>
              <td className={tdCls}>
                <span className="font-mono text-xs">
                  {o.invoiceNumber ?? t('collection.payments.onAccount')}
                </span>
              </td>
              <td className={tdCls}>
                <StatePill state={o.reversed ? 'REJETE' : o.pending ? 'PRESENTE' : 'RECU'} />
              </td>
              <td className={tdCls}>
                {canAct && !o.reversed && (
                  <div className="flex justify-end gap-1">
                    {o.pending && (
                      <Button size="sm" variant="outline" onClick={() => finalise(o)}>
                        {t('collection.payments.confirm')}
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setReversing(o)}>
                      {t('collection.payments.cancel')}
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reversing && (
        <Modal
          title={t('collection.payments.cancelTitle', { amount: formatEUR(reversing.amount) })}
          lede={t('collection.payments.rejectLede')}
          onClose={() => setReversing(null)}
          footer={
            <>
              <Button variant="outline" onClick={() => setReversing(null)}>
                {t('collection.common.cancel')}
              </Button>
              <Button variant="destructive" onClick={reverse} disabled={!reason.trim()}>
                {t('collection.payments.cancel')}
              </Button>
            </>
          }
        >
          <label className="block">
            <span className={labelCls}>{t('collection.col.reason')}</span>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </label>
        </Modal>
      )}
    </>
  );
}
